import { useState, useMemo, useCallback } from 'react';
import { Box, Button, CircularProgress, Pagination, Typography, Chip } from '@mui/material';
import { Psychology, Refresh, ThumbUp } from '@mui/icons-material';
import { enhancedApi as adminApi } from '@store/generated/admin';
import type { SessionListItemResponse } from '@store/generated/admin';
import { DataTable } from './data-table';
import { JotCoachSessionDetailDialog } from './jotcoach-session-detail-dialog';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';

const { useGetV2AdminJotcoachSessionsQuery } = adminApi;

interface OrganizationJotCoachSectionProps {
  organizationId: string;
}

export function OrganizationJotCoachSection({ organizationId }: OrganizationJotCoachSectionProps) {
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const {
    data: sessionsData,
    isLoading,
    error,
    refetch,
  } = useGetV2AdminJotcoachSessionsQuery({
    organizationId,
    page,
    perPage,
  });

  const sessions = sessionsData?.items || [];
  const totalPages = sessionsData?.total_pages || 0;
  const total = sessionsData?.total || 0;

  const handlePageChange = useCallback((_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  }, []);

  const handleRowClick = useCallback((session: SessionListItemResponse) => {
    setSelectedSessionId(session.jotcoach_session_id);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedSessionId(null);
  }, []);

  const columns = useMemo(
    () => [
      {
        key: 'jotcoach_session_id' as keyof SessionListItemResponse,
        label: 'Session ID',
        render: (value: unknown) => (
          <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
            {String(value).substring(0, 8)}...
          </Typography>
        ),
      },
      {
        key: 'user_name' as keyof SessionListItemResponse,
        label: 'User',
        render: (_value: unknown, row: SessionListItemResponse) => (
          <Box>
            <Typography variant="body2">{row.user_name || 'Unknown'}</Typography>
            {row.user_email && (
              <Typography variant="caption" color="text.secondary" display="block">
                {row.user_email}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'note_id' as keyof SessionListItemResponse,
        label: 'Note ID',
        render: (value: unknown) => (
          <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
            {value ? `${String(value).substring(0, 8)}...` : 'N/A'}
          </Typography>
        ),
      },
      {
        key: 'created_at' as keyof SessionListItemResponse,
        label: 'Created',
        render: (value: unknown) => (
          <Box>
            <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(value as string)}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'has_sms' as keyof SessionListItemResponse,
        label: 'SMS Sent',
        render: (value: unknown) => (
          <Typography variant="body2" color={value ? 'success.main' : 'text.secondary'}>
            {value ? 'Sent' : 'Not sent'}
          </Typography>
        ),
      },
      {
        key: 'feedback_count' as keyof SessionListItemResponse,
        label: 'Feedback',
        render: (value: unknown) => {
          const count = typeof value === 'number' ? value : 0;
          return count > 0 ? (
            <Chip
              icon={<ThumbUp />}
              label={`${count} ${count === 1 ? 'item' : 'items'}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No feedback
            </Typography>
          );
        },
      },
      {
        key: 'updated_at' as keyof SessionListItemResponse,
        label: 'Last Updated',
        render: (value: unknown) => (
          <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Psychology fontSize="large" color="primary" />
          <Box>
            <Typography variant="h5" fontWeight="medium">
              JotCoach Sessions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AI coaching sessions for billing optimization
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={isLoading ? <CircularProgress size={16} /> : <Refresh />}
          onClick={() => refetch()}
          disabled={isLoading}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Box py={4} textAlign="center">
          <Typography color="error">Failed to load JotCoach sessions</Typography>
        </Box>
      )}

      <DataTable
        title=""
        columns={columns}
        data={sessions}
        total={total}
        loading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No JotCoach sessions found for this organization"
      />

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <JotCoachSessionDetailDialog
        open={selectedSessionId !== null}
        onClose={handleCloseDialog}
        sessionId={selectedSessionId}
      />
    </Box>
  );
}
